/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      pure lib — the one decision of the payment return pages (payment-status,
 *              payment-request): which outcome the customer is looking at, and whether
 *              a second payment may be offered from it.
 *   uses:      -
 *   users:     controllers/payment-status.js, controllers/payment-request.js (and, via
 *              their getters, templates/payment-status.hbs, templates/payment-request.hbs,
 *              components/checkout-nuova/outcome.hbs)
 *   tests:     tests/unit/lib/payment-outcome-test.js
 *   gotchas:   'checking' NEVER offers a retry: the bank has not answered yet, so a card
 *              charge may already be under way and a second payment would charge twice.
 *              The retry appears on a verified failure, on a bank transfer on hold (nothing
 *              is charged automatically), or once the verification poll gave up
 *              ('timeout'), and then only next to a warning to check before paying again.
 */

// The return page re-reads the payment this often while the bank's answer is pending...
export const PAYMENT_POLL_INTERVAL_MS = 5000;
// ...and gives up (timeout outcome: warning + retry) after this many reads, about one minute.
export const PAYMENT_POLL_MAX_ATTEMPTS = 12;

export const PAYMENT_OUTCOME = Object.freeze({
  SUCCESS: 'success',
  FAILED: 'failed',
  ON_HOLD: 'onhold',
  CHECKING: 'checking',
  TIMEOUT: 'timeout',
});

/**
 * @param {object} state
 * @param {boolean} state.success   the payment is completed
 * @param {boolean} state.failed    the bank answered and the payment failed
 * @param {boolean} state.bonifico  bank transfer: on hold until the school receives it
 * @param {boolean} state.timedOut  the verification poll gave up without an answer
 * @returns {string} one of PAYMENT_OUTCOME
 */
export function paymentOutcome({ success, failed, bonifico, timedOut } = {}) {
  if (success) return PAYMENT_OUTCOME.SUCCESS;
  if (failed) return PAYMENT_OUTCOME.FAILED;
  if (bonifico) return PAYMENT_OUTCOME.ON_HOLD;
  return timedOut ? PAYMENT_OUTCOME.TIMEOUT : PAYMENT_OUTCOME.CHECKING;
}

/** Whether a second payment may be offered for this outcome (see the MAP gotcha). */
export function canRetryPayment(outcome) {
  return (
    outcome === PAYMENT_OUTCOME.FAILED ||
    outcome === PAYMENT_OUTCOME.ON_HOLD ||
    outcome === PAYMENT_OUTCOME.TIMEOUT
  );
}
